import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { 
  Upload, X, Crop, Check, RotateCcw, ZoomIn, ZoomOut, 
  Image as ImageIcon, Link, Loader2, Plus, GripVertical, 
  Video, Instagram, Play, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Slider } from './slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

const API = process.env.REACT_APP_BACKEND_URL;

// Aspect ratio presets
const aspectRatioPresets = [
  { value: 'free', label: 'Free Crop', ratio: undefined },
  { value: '1:1', label: 'Square (1:1)', ratio: 1 },
  { value: '16:9', label: 'Landscape (16:9)', ratio: 16/9 },
  { value: '9:16', label: 'Portrait (9:16)', ratio: 9/16 },
  { value: '4:3', label: 'Standard (4:3)', ratio: 4/3 },
  { value: '3:2', label: 'Photo (3:2)', ratio: 3/2 },
];

export const MultiMediaUploader = ({ 
  images = [],
  videos = [],
  onImagesChange,
  onVideosChange,
  label = "Media Gallery",
  maxImages = 10,
  maxVideos = 5,
  showVideos = true,
  className = "",
  token
}) => {
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('free');
  const [urlInput, setUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  const [videoModal, setVideoModal] = useState({ open: false, url: '', type: 'instagram' });
  const fileInputRef = useRef(null);

  const getCurrentAspect = () => {
    const found = aspectRatioPresets.find(ar => ar.value === selectedAspectRatio);
    return found?.ratio; // undefined means free crop
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max limit
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Process first file for cropping
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop({
        file,
        preview: reader.result,
        remainingFiles: files.slice(1)
      });
      setCropModalOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setSelectedAspectRatio('free');
    };
    reader.readAsDataURL(file);
    
    e.target.value = '';
  };

  const uploadCroppedImage = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageToCrop.file);
      
      const img = new window.Image();
      img.src = imageToCrop.preview;
      await new Promise(resolve => img.onload = resolve);
      
      const cropX = (croppedAreaPixels.x / img.width) * 100;
      const cropY = (croppedAreaPixels.y / img.height) * 100;
      const cropWidth = (croppedAreaPixels.width / img.width) * 100;
      const cropHeight = (croppedAreaPixels.height / img.height) * 100;
      
      formData.append('crop_x', cropX.toString());
      formData.append('crop_y', cropY.toString());
      formData.append('crop_width', cropWidth.toString());
      formData.append('crop_height', cropHeight.toString());

      const response = await fetch(`${API}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      
      // Add to images array
      const newImages = [...images, data.url];
      onImagesChange(newImages);
      
      // Check if there are more files to process
      if (imageToCrop.remainingFiles && imageToCrop.remainingFiles.length > 0) {
        const nextFile = imageToCrop.remainingFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          setImageToCrop({
            file: nextFile,
            preview: reader.result,
            remainingFiles: imageToCrop.remainingFiles.slice(1)
          });
          setCrop({ x: 0, y: 0 });
          setZoom(1);
        };
        reader.readAsDataURL(nextFile);
        toast.success(`Image uploaded! ${imageToCrop.remainingFiles.length} more to go`);
      } else {
        setCropModalOpen(false);
        setImageToCrop(null);
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    try {
      new URL(urlInput);
      if (images.length >= maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }
      onImagesChange([...images, urlInput]);
      setUrlInput('');
      toast.success('Image URL added');
    } catch {
      toast.error('Please enter a valid URL');
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    onImagesChange(newImages);
  };

  // Video handling
  const addVideo = () => {
    if (!videoModal.url.trim()) {
      toast.error('Please enter a video URL');
      return;
    }

    // Validate URL based on type
    let isValid = false;
    if (videoModal.type === 'instagram') {
      isValid = videoModal.url.includes('instagram.com');
    } else if (videoModal.type === 'youtube') {
      isValid = videoModal.url.includes('youtube.com') || videoModal.url.includes('youtu.be');
    } else {
      try {
        new URL(videoModal.url);
        isValid = true;
      } catch {
        isValid = false;
      }
    }

    if (!isValid) {
      toast.error('Please enter a valid video URL');
      return;
    }

    if (videos.length >= maxVideos) {
      toast.error(`Maximum ${maxVideos} videos allowed`);
      return;
    }

    const newVideo = {
      url: videoModal.url,
      type: videoModal.type,
      id: `video-${Date.now()}`
    };

    onVideosChange([...videos, newVideo]);
    setVideoModal({ open: false, url: '', type: 'instagram' });
    toast.success('Video added successfully!');
  };

  const removeVideo = (index) => {
    const newVideos = videos.filter((_, i) => i !== index);
    onVideosChange(newVideos);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Label className="text-base font-semibold">{label}</Label>
      
      {/* Images Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {images.map((url, index) => (
            <div 
              key={index} 
              className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
            >
              <img 
                src={url} 
                alt={`Image ${index + 1}`} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" font-family="sans-serif" font-size="10" fill="%2394a3b8" text-anchor="middle" dy=".3em">Error</text></svg>';
                }}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {index > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="w-7 h-7"
                    onClick={() => moveImage(index, index - 1)}
                  >
                    <GripVertical className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="w-7 h-7"
                  onClick={() => removeImage(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {index === 0 && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-sky-500 text-white text-[10px] font-medium rounded">
                  Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Videos Preview */}
      {showVideos && videos.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Videos ({videos.length})</Label>
          <div className="flex flex-wrap gap-2">
            {videos.map((video, index) => (
              <div 
                key={video.id || index} 
                className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg border"
              >
                {video.type === 'instagram' && <Instagram className="w-4 h-4 text-pink-500" />}
                {video.type === 'youtube' && <Play className="w-4 h-4 text-red-500" />}
                {video.type === 'direct' && <Video className="w-4 h-4 text-sky-500" />}
                <span className="text-sm text-slate-700 max-w-[150px] truncate">{video.url}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="w-6 h-6 text-red-500 hover:text-red-600"
                  onClick={() => removeVideo(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Options */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${showVideos ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <TabsTrigger value="upload" className="text-xs">
            <Upload className="w-3 h-3 mr-1" /> Upload
          </TabsTrigger>
          <TabsTrigger value="url" className="text-xs">
            <Link className="w-3 h-3 mr-1" /> Image URL
          </TabsTrigger>
          {showVideos && (
            <TabsTrigger value="video" className="text-xs">
              <Video className="w-3 h-3 mr-1" /> Video Link
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="upload" className="mt-2">
          <div 
            className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Click to upload images</p>
            <p className="text-xs text-slate-400 mt-1">
              JPG, PNG, WebP up to 10MB • {images.length}/{maxImages} images
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </TabsContent>
        
        <TabsContent value="url" className="mt-2">
          <div className="flex gap-2">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1"
            />
            <Button type="button" onClick={handleUrlSubmit} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </TabsContent>

        {showVideos && (
          <TabsContent value="video" className="mt-2">
            <div className="space-y-3">
              <Select 
                value={videoModal.type} 
                onValueChange={(value) => setVideoModal({ ...videoModal, type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select video type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">
                    <span className="flex items-center gap-2">
                      <Instagram className="w-4 h-4 text-pink-500" /> Instagram Reel
                    </span>
                  </SelectItem>
                  <SelectItem value="youtube">
                    <span className="flex items-center gap-2">
                      <Play className="w-4 h-4 text-red-500" /> YouTube
                    </span>
                  </SelectItem>
                  <SelectItem value="direct">
                    <span className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-sky-500" /> Direct URL
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input
                  value={videoModal.url}
                  onChange={(e) => setVideoModal({ ...videoModal, url: e.target.value })}
                  placeholder={
                    videoModal.type === 'instagram' ? 'https://instagram.com/reel/...' :
                    videoModal.type === 'youtube' ? 'https://youtube.com/watch?v=...' :
                    'https://example.com/video.mp4'
                  }
                  className="flex-1"
                />
                <Button type="button" onClick={addVideo} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {videos.length}/{maxVideos} videos added
              </p>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Crop Modal with Free Crop */}
      <Dialog open={cropModalOpen} onOpenChange={(open) => !uploading && setCropModalOpen(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="w-5 h-5" /> Crop Image
              {imageToCrop?.remainingFiles?.length > 0 && (
                <span className="text-sm font-normal text-slate-500">
                  ({imageToCrop.remainingFiles.length} more after this)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Aspect Ratio Selector */}
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Select Crop Mode:</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {aspectRatioPresets.map((ar) => (
                <button
                  key={ar.value}
                  type="button"
                  onClick={() => {
                    setSelectedAspectRatio(ar.value);
                    setCrop({ x: 0, y: 0 });
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    selectedAspectRatio === ar.value
                      ? 'bg-sky-500 text-white shadow-md'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                  }`}
                >
                  {ar.label}
                </button>
              ))}
            </div>
            {selectedAspectRatio === 'free' && (
              <p className="text-xs text-green-600">✓ Free crop enabled - drag corners to resize</p>
            )}
          </div>

          <div className="relative h-80 bg-slate-900 rounded-lg overflow-hidden">
            {imageToCrop && (
              <Cropper
                image={imageToCrop.preview}
                crop={crop}
                zoom={zoom}
                aspect={getCurrentAspect()}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={true}
                cropShape="rect"
              />
            )}
          </div>

          {/* Zoom Control */}
          <div className="flex items-center gap-4 px-4">
            <ZoomOut className="w-4 h-4 text-slate-500" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={([value]) => setZoom(value)}
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-slate-500" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setCropModalOpen(false);
                setImageToCrop(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button 
              onClick={uploadCroppedImage}
              disabled={uploading}
              className="bg-sky-500 hover:bg-sky-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {imageToCrop?.remainingFiles?.length > 0 ? 'Crop & Next' : 'Crop & Upload'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultiMediaUploader;
